import axios from "axios";
import { API_BASE_URL } from "utils/api";

const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  headers: {
    "ngrok-skip-browser-warning": "true",
    "Content-Type": "application/json",
  },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

/**
 * Fetch folders for leads or clients
 * @param {string} type - "Lead" or "Client"
 * @param {number} page - current page
 * @param {number} limit - items per page
 * @returns {Promise<Object>} folders data
 */
export const getFolders = async (type = "Lead", page = 1, limit = 10) => {
  const folderType = type === "Client" ? "Client" : "Lead";
  const endpoint = folderType === "Client" ? "/folders/clients" : "/folders";

  const response = await api.get(endpoint, {
    params: {
      favoritePage: page,
      allPage: page,
      favoritePageLimit: limit,
      allPageLimit: limit,
      type: folderType,
    },
  });

  const data = response?.data?.data || {};

  return {
    allFolders: data.allFolders || { data: [], total: 0, page, pageLimit: limit },
    favoriteFolders: data.favoriteFolders || { data: [], total: 0, page, pageLimit: limit },
    message: response?.data?.message || "Folders retrieved successfully",
    success: response?.data?.success || true,
  };
};

/**
 * Toggle favorite status for a folder
 * @param {string} folderId - folder ID
 * @param {boolean} isFavorite - current favorite status (will be toggled)
 * @returns {Promise<Object>} updated folder
 */
export const toggleFavoriteFolder = async (folderId, isFavorite) => {
  const response = await api.patch(`/folders/${folderId}/toggle-favorite`);

  return response?.data?.data || {};
};

/**
 * Delete a folder
 * @param {string} folderId - folder ID
 * @returns {Promise<Object>} response
 */
export const deleteFolder = async (folderId) => {
  const response = await api.delete(`/folders/${folderId}`);
  return response?.data?.data || {};
};

/**
 * Fetch folder contents (leads/clients inside a folder)
 * @param {string} folderId - folder ID
 * @param {number} page - current page
 * @param {number} limit - items per page
 * @returns {Promise<Object>} folder contents data
 */
export const getFolderContents = async (folderId, page = 1, limit = 20) => {
  const response = await api.get(`/folders/${folderId}/contents`, {
    params: { page, limit },
  });

  return {
    items: response?.data?.data?.data || [],
    total: response?.data?.data?.total || 0,
    leadCount: response?.data?.data?.leadCount || 0,
    clientCount: response?.data?.data?.clientCount || 0,
    folderName: response?.data?.data?.folderName || "",
    message: response?.data?.message || "Contents retrieved successfully",
    success: response?.data?.success || true,
  };
};

/**
 * Create a new folder
 * @param {Object} folderData - folder data
 * @param {string} folderData.name - folder name
 * @param {string} folderData.type - "Lead" or "Client"
 * @param {Object} folderData.gradient - gradient config
 * @returns {Promise<Object>} created folder
 */
export const createFolder = async (folderData) => {
  const response = await api.post("/folders", folderData);
  return response?.data?.data || {};
};
